/**
 * SIP 明细的**中文标签**映射 —— 唯一出处（v0.51）。
 *
 * ## 为什么要有这个文件
 *
 * 界面原来**直接显示内部枚举**：`类别` 列 29 行里 26 行是 `dimension`、
 * `类型` 列全是 `linear`，行级警告里是 `unknown_gdt` / `surface_profile`。
 * 质量部同事看不懂这些词；更糟的是 `unknown_gdt` 表示**识别没认出来**，
 * 却和正常行长得一模一样 —— 看不出哪几行需要人工确认。
 *
 * ⚠️ **`type` 是「随 `kind` 变语义」的 overloaded 字段**，不能建一张平表：
 *   · `kind=dimension` 时 `type` 是**尺寸类型**：`linear`=线性 / `radius`=半径；
 *   · `kind=gdt`      时 `type` 是**公差类型**：`position`=位置度 / `flatness`=平面度 …
 *   同一个 `linear` 在 `gdt` 下是「直线度」，在 `dimension` 下是「线性尺寸」。
 *   ⇒ 查表键必须是 `${kind}|${type}`。
 *
 * ## 🔴 兜底纪律：**宁可显示原值，也不留空白、更不猜**
 *
 * 新增一个我们没收录的公差类型时，**不能**让它显示成空白（用户会以为是 bug）
 * 也不能猜一个中文（那就是「兜底推断把空白变成有把握的错值」）。
 * ⇒ 一律**原样显示内部值**，并由 `UNKNOWN_GDT` 单独标「未识别」徽标。
 */
import type { SipRow } from './SipItemsTable';
// v0.68：「类别」的值与中文标签下沉到 `lib/sipKind.ts`（理由见该文件头 ——
// store 与组件都要用它，放在组件里会形成循环 import）。
import { KIND_ZH, kindLabel } from '../../lib/sipKind';

// 原样转出（调用方仍从本文件 import，签名不变）
export { KIND_ZH, kindLabel } from '../../lib/sipKind';

/** 识别没认出来的公差类型（**不是**「未知类别」，是「这一行没搞定」）。 */
export const UNKNOWN_GDT = 'unknown_gdt';

/** 公差类型中文表。键 = `${kind}|${type}` —— 见文件头「type 是 overloaded 字段」。 */
const TYPE_ZH: Record<string, string> = {
  // ── 尺寸类型
  'dimension|linear': '线性',
  'dimension|radius': '半径',
  'dimension|diameter': '直径',
  // ── 形位公差类型
  'gdt|position': '位置度',
  'gdt|flatness': '平面度',
  'gdt|parallelism': '平行度',
  'gdt|perpendicularity': '垂直度',
  'gdt|cylindricity': '圆柱度',
  'gdt|circularity': '圆度',
  'gdt|concentricity': '同心度',
  'gdt|coaxiality': '同轴度',
  'gdt|symmetry': '对称度',
  'gdt|surface_profile': '表面轮廓度',
  'gdt|line_profile': '线轮廓度',
  'gdt|angularity': '倾斜度',
  'gdt|true_position': '真实位置度',
  // ── 螺纹 / 粗糙度
  'thread|thread': '螺纹',
  'surface_roughness|roughness': '粗糙度',
};

/**
 * 第二查：**只看 `type` 名**（`kind` 与 `type` 不配套时的兜底）。
 *
 * 🔴 为什么需要它：实测 `-02制程` 里有行的 `kind='dimension'` 而
 *    `type='flatness' / 'surface_profile' / 'unknown_gdt'` —— 上游分配
 *    把「没识别出标准值」的行统一按 dimension 归类，`${kind}|${type}`
 *    这一键就**永远查不到**，界面又把英文原样印回给用户（实测踩到）。
 *
 * ⚠️ 仍然是**精确同名**匹配，绝不做「包含/相似」猜测 ——
 *    猜错就是「把空白变成有把握的错值」。
 */
const TYPE_ZH_BY_NAME: Record<string, string> = {
  linear: '线性', radius: '半径', diameter: '直径',
  position: '位置度', flatness: '平面度', parallelism: '平行度',
  perpendicularity: '垂直度', cylindricity: '圆柱度', circularity: '圆度',
  concentricity: '同心度', coaxiality: '同轴度', symmetry: '对称度',
  surface_profile: '表面轮廓度', line_profile: '线轮廓度',
  angularity: '倾斜度', true_position: '真实位置度',
  thread: '螺纹', roughness: '粗糙度', datum: '基准',
  [UNKNOWN_GDT]: '未识别',
};

/** `kind` → 中文；空 → 空串；未收录 → **原样返回**。
 *
 *  ⚠️ v0.68 起实现已下沉到 `lib/sipKind.ts`（`store` 也要用这张表，留在组件里
 *     会形成循环 import），本文件只做转出。行为一字未变。 */

/** `type` → 中文；**优先**带 `kind` 的键，再退回按名精确查；仍未收录 → **原样返回**。 */
export function typeLabel(kind: string, type: string): string {
  const k = (kind ?? '').trim();
  const t = (type ?? '').trim();
  if (!t) return '';
  return TYPE_ZH[`${k}|${t}`] ?? TYPE_ZH_BY_NAME[t] ?? t;
}

/** 这一行是不是「识别没认出来」⇒ 界面要标「未识别」徽标。 */
export function isUnrecognized(row: { kind: string; type: string }): boolean {
  return (row.type ?? '').trim() === UNKNOWN_GDT;
}

/**
 * 「特征」一格的合成文案：`尺寸 · 线性` / `形位公差 · 位置度` / 空。
 * 任一半为空就只显示另一半（不产生 `尺寸 · ` 这种带尾分隔符的半截话）。
 */
export function featureLabel(row: Pick<SipRow, 'kind' | 'type'>): string {
  const a = kindLabel(row.kind);
  const b = typeLabel(row.kind, row.type);
  if (a && b) return `${a} · ${b}`;
  return a || b;
}

/**
 * 行级警告的**本地化**。
 *
 * 后端 `sip_fill.build_items` 产出的 `warn` 是诊断文案（**不进导出**，
 * 适配器写 xlsx 时一律置空），形如：
 *   `无标准值（surface_profile）⇒ G 留空待人工确认`
 * 括号里是**内部英文枚举**，质量部同事看不懂。
 *
 * ⚠️ **只替换枚举、不改写整句**：中文映射的唯一出处仍是本文件的 `TYPE_ZH`
 *   （不在这里再写一张表 = 「同一语义几份实现」）。
 *   句子里别的东西（`G 留空` 指的是导出 xlsx 里那一格）原样保留。
 */
export function localizeWarn(w: string, kind: string): string {
  if (!w) return '';
  return w.replace(/（([a-z_]+)）/g, (_m, t: string) => `（${typeLabel(kind, t)}）`);
}

/* ================================================================
 * v0.65：行级警告要**跟着标准值走**（不能停在打开这一份时的快照上）
 * ================================================================ */

/** 「无标准值」那一条诊断的开头 —— 与后端 `sip_fill.NO_STD_PREFIX` 同一个词。 */
export const NO_STD_PREFIX = '无标准值';

/** 行级警告的**分段**：后端把多条诊断用「；」拼成一行（无标准值 / 公差说明…）。 */
export function splitWarn(w: string): string[] {
  return String(w ?? '').split('；').map((s) => s.trim()).filter(Boolean);
}

/**
 * 「无标准值」那一条的完整文案（前端侧的**唯一**出处）。
 *
 * 🔴 必须与后端 `sip_fill.no_std_warn()` 逐字一致 —— 有护栏
 *    `backend/tests/test_sip_warn_v065.py` 比对两侧的固定片段，改一边必红。
 *    为什么两边都要有：后端负责写库与接口，前端必须能在用户**刚**把标准值
 *    清空的那一刻就把提示补出来，而不是等一次保存往返。
 */
export function noStdWarn(kind: string, type: string): string {
  return `${NO_STD_PREFIX}（${typeLabel(kind, type) || '?'}）⇒ G 留空待人工确认`;
}

/**
 * 行级警告的**显示文案** —— 按「标准值填没填」现算（v0.65）。
 *
 * 用户 2026-10-17 原话：
 *   「第 9 行无标准值（位置度）⇒ G 留空待人工确认，我修改了标准值后
 *    为什么不能及时更新并消失，应该同步阿」
 *
 * 真因：`warn` 是**打开这一份时后端给的快照**，编辑动作不会重算它 ⇒
 * 标准值填上了，那句「无标准值」还挂着；更糟的是它会被原样提交回去
 * （`toWire` 发 `r.warn`），存过草稿之后**还在**（实测库里 3 行 nominal
 * 已有值、`warn` 仍是「无标准值」）。所以两边都得按当前状态重算：
 * 前端负责"立刻反应"，后端 `sip_draft_service._sync_no_std_warn()` 负责落库。
 *
 * 口径：这一条**只跟 `is_dim` 走**（`is_dim` = 标准值非空；后端也按
 * `nominal != null` 派生，见服务层纪律 2）。其余诊断（公差说明之类）
 * **原样保留、一条不动** —— 它们不是由标准值推出来的，删了就是丢信息。
 *
 * ⚠️ 刚点「+ 空行」加出来的那一行**不提示**：`kind` 与 `type` 都还是空，
 *    这时候说「无标准值（?）」纯属噪音（用户加一行"外观/包装"文案也会中招）。
 *    等他把类别/类型选上，提示才出现 —— 那一行此时才算"一条检验项目"。
 */
export function effectiveWarn(
  row: Pick<SipRow, 'warn' | 'kind' | 'type' | 'is_dim'>,
): string {
  const rest = splitWarn(row.warn).filter((p) => !p.startsWith(NO_STD_PREFIX));
  const meaningful = !!((row.kind || '').trim() || (row.type || '').trim());
  if (!row.is_dim && meaningful) rest.unshift(noStdWarn(row.kind, row.type));
  return rest.map((p) => localizeWarn(p, row.kind)).join('；');
}

/* ================================================================
 * 下面是「保能力」的部分：kind / type 仍然**可编辑**，只是从「英文自由文本」
 * 换成「中文下拉」。
 *
 * ⚠️ 为什么不用只读文本：项目纪律「撤列只撤列、不撤能力」——
 *   把可编辑字段改成只读就等于**撤掉了能力**。所以做成 `<select>`：
 *   界面显示中文，`value` 仍是内部枚举 ⇒ 导出/统计口径一个字都不变。
 * ================================================================ */

/**
 * `kind` 下拉项 —— v0.68 起**值来自后台清单**（`sip_kind`），不再写死在前端。
 *
 * 🔴 两条兜底纪律（与 `typeOptions` 同源）：
 *  1. **`current` 不在列表里就必须补进选项**。否则 `<select>` 的当前值不在
 *     选项内 ⇒ 浏览器显示**空白**，用户一保存就把这个值**悄悄抹掉**了
 *     （历史行里可能是更早版本写的值、或刚被停用的值）。
 *  2. **按标签去重**：用户自己加的「尺寸」会与预置的 `dimension` 显示成同一个词，
 *     两个一模一样的选项选哪个都像 bug。保留先出现的（后端按 `sort_order` 排，
 *     预置项在前）。⚠️ 但若 `current` 恰好被去重掉，仍按纪律 1 补回。
 */
export function kindOptions(
  values: string[], current = '',
): Array<{ value: string; label: string }> {
  const seen = new Set<string>();
  const opts: Array<{ value: string; label: string }> = [];
  for (const raw of values) {
    const value = (raw ?? '').trim();
    if (!value) continue;
    const label = kindLabel(value);
    if (seen.has(label)) continue;   // 同名标签只留第一个（预置优先）
    seen.add(label);
    opts.push({ value, label });
  }
  const cur = (current ?? '').trim();
  if (cur && !opts.some((o) => o.value === cur)) {
    opts.push({ value: cur, label: kindLabel(cur) });
  }
  return opts;
}

/** 各类别下的 `type` 选项（内部枚举全集的中文可选项）。 */
const TYPES_BY_KIND: Record<string, string[]> = {
  dimension: ['linear', 'radius', 'diameter'],
  gdt: [
    'position', 'flatness', 'parallelism', 'perpendicularity', 'cylindricity',
    'circularity', 'concentricity', 'coaxiality', 'symmetry',
    'surface_profile', 'line_profile', 'angularity', 'true_position',
    UNKNOWN_GDT,
  ],
  thread: ['thread'],
  reference: ['datum'],
  surface_roughness: ['roughness'],
};

/**
 * 某个 `kind` 下的 `type` 下拉项。
 *
 * 🔴 **兜底纪律**：库里出现了一个我们没收录的 `type` 时，**必须把它补进列表**，
 * 否则 `<select>` 选中值不在选项里 ⇒ 浏览器显示**空白**，
 * 用户一保存就把这个值**悄悄抹掉**了。所以这里显式追加当前值。
 */
export function typeOptions(kind: string, current: string): Array<{ value: string; label: string }> {
  const list = TYPES_BY_KIND[(kind ?? '').trim()] ?? [];
  const cur = (current ?? '').trim();
  // ⚠️ 标签**必须**走 `typeLabel`（唯一出处），不要在这里另查一张表
  const opts = list.map((t) => ({ value: t, label: typeLabel(kind, t) }));
  if (cur && !opts.some((o) => o.value === cur)) {
    opts.push({ value: cur, label: cur });
  }
  return opts;
}